import React, { useMemo, useState } from "react"
import { graphql, useFragment } from "react-relay"
import { useBlockLayout, useTable } from "react-table"
import { useDebouncedCallback } from "use-debounce"

import styles from "./styles.module.css"

import { IntegrationImportProjectsTable_ext_project$key } from "./__generated__/IntegrationImportProjectsTable_ext_project.graphql"

import { SelectorSearch } from "~/common/SelectorModal"
import TableVirtualized from "~/common/TableVirtualized"
import Checkbox from "~/common/inputs/Checkbox"

type Props = {
  extProjectKey: IntegrationImportProjectsTable_ext_project$key
  checkedSet: Set<number>
  showPreviouslyImported?: boolean
  previouslyImported?: Set<number>
  onChange: (checkedSet: Set<number>) => void
}

const IntegrationImportProjectsTable = (props: Props) => {
  const extProjectList = useFragment(
    graphql`
      fragment IntegrationImportProjectsTable_ext_project on ext_project
      @relay(plural: true) {
        id
        name
        sync
        remote_id
        remote_key
      }
    `,
    props.extProjectKey,
  )

  const {
    checkedSet,
    onChange,
    showPreviouslyImported = false,
    previouslyImported,
  } = props

  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("")

  const hasKeys = useMemo(
    () =>
      extProjectList.every((extProject) => extProject.remote_key !== undefined),
    [extProjectList],
  )
  const filteredExtProjects = useMemo(
    () => {
      return extProjectList.filter(
        (extProject) =>
          (showPreviouslyImported || !extProject.sync) &&
          `${hasKeys ? extProject.remote_key : extProject.remote_id}${extProject.name}`
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extProjectList, debouncedSearchValue],
  )

  const isAllEnabled = useMemo(
    () =>
      filteredExtProjects.every((extProject) =>
        checkedSet.has(extProject.id),
      ) && filteredExtProjects.length > 0,
    [checkedSet, filteredExtProjects],
  )

  const handleSearchChange = useDebouncedCallback((value: string) => {
    setDebouncedSearchValue(value)
  }, 150)

  const handleSearchInputChange = (value: string) => {
    setSearchValue(value)
    handleSearchChange(value)
  }

  const handleToggleAll = (enabled: boolean) => {
    const nextCheckedSet = new Set<number>(checkedSet)
    for (const extProject of filteredExtProjects) {
      if (enabled) {
        nextCheckedSet.add(extProject.id)
      } else {
        nextCheckedSet.delete(extProject.id)
      }
    }
    onChange(nextCheckedSet)
  }

  const handleToggle = (id: number) => {
    const nextCheckedSet = new Set<number>(checkedSet)
    if (nextCheckedSet.has(id)) {
      nextCheckedSet.delete(id)
    } else {
      nextCheckedSet.add(id)
    }
    onChange(nextCheckedSet)
  }

  const columns = React.useMemo(
    () => [
      {
        id: "Project Code",
        Header: () => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              id="select-all"
              onChange={() => handleToggleAll(!isAllEnabled)}
              checked={isAllEnabled}
              style={{ marginRight: "10px" }}
            />
            <div>Project Code</div>
          </div>
        ),
        accessor: (row) => row.remote_key || row.remote_id,
        Cell: ({ cell }) => {
          const id = cell.row.original.id
          const isPreviouslyImported = previouslyImported?.has(id)
          const checked = checkedSet.has(id) || isPreviouslyImported
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                id={id.toString()}
                onChange={() => handleToggle(id)}
                checked={checked}
                disabled={isPreviouslyImported}
                style={{ marginRight: "10px" }}
              />
              <div>{cell.value}</div>
            </div>
          )
        },
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ cell }) => <div>{cell.value}</div>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedSet, isAllEnabled, filteredExtProjects, previouslyImported],
  )

  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 250,
    }),
    [],
  )

  const table = useTable(
    {
      columns,
      data: filteredExtProjects,
      defaultColumn,
    },
    useBlockLayout,
  )
  return (
    <div className={styles.container}>
      <SelectorSearch
        placeholder="Search Projects"
        searchValue={searchValue}
        setSearchValue={handleSearchInputChange}
      />
      <TableVirtualized table={table} height={400} minHeight={60} />
    </div>
  )
}

export { IntegrationImportProjectsTable }
